<template lang="pug">
.FImageTrail(@mousemove="updateBoxShadow")
  .FImageTrail__words
    div(
      v-for="word in words"
      :key="word"
    ) {{ word }}
  .FImageTrail__images
    .FImageTrail__image(
      v-for="(image, index) in images"
      :key="image.alt"
      :ref="refs.set"
      :style="styles[index]"
    )
      FImage(v-bind="{ ...image, height: '100%', corners: 'sharp' }")
</template>

<style lang="stylus">
.FImageTrail
  position relative
  margin rem(80) 0
  overflow-x clip

  +media-down('sm')
    margin-bottom 60vh

.FImageTrail__images
  margin auto
  max-width rem(1080)
  margin-top -100vh

  & > *
    position relative
    margin-bottom rem(-50)
    margin-left rem(100)
    box-shadow 0 0 50px 10px rgba(0, 0, 0, 0.1)

    &:nth-child(2n)
      margin-left auto
      margin-right rem(100)

    for i in 1 .. 15
      &:nth-child({i})
        z-index 15 - i

.FImageTrail__words
  position sticky
  top rem(50)
  color var(--color--neutral--light-2)
  text-align right
  user-select none
  font-size 13vh
  font-weight bold
  letter-spacing rem(-2)
  white-space nowrap

  +media-down('sm')
    font-size 8vh

.FImageTrail__image
  width fit-content
  max-width rem(1080)

  +media-down('md')
    max-width rem(880)

  +media-down('sm')
    max-width rem(540)

  +media-down('xs')
    max-width rem(320)
</style>

<script setup lang="ts">
import { useTemplateRefsList, useEventListener } from '@vueuse/core';

import { clamp, lerp } from '@/lib/utils/math';

import type { FImageProps } from '@fifteen/design-system-vue';

export interface FImageTrailProps {
  /**
   * Array of images
   */
  images: FImageProps[];
  /**
   * Array of words
   */
  words?: string[];
}

withDefaults(defineProps<FImageTrailProps>(), {
  words: () => [],
});

const refs = useTemplateRefsList<HTMLDivElement>();
const frames = ref<number[]>([]);

const { isSmAndDown } = useFBreakpoints();
const styles = ref<Style[]>([]);

const translateX = computed(() => (isSmAndDown.value ? 75 : 400));
const translateY = 200;
const rotateX = -15;
const rotateZ = -15;

function update(i: number): void {
  const element = refs.value[i];
  const rect = element.getBoundingClientRect();
  if (!rect) return;

  const percentage = clamp(
    (rect.top + rect.height / 2) / (window.innerHeight + rect.height),
    0,
    1
  );

  const currentTranslateX = parseFloat(
    styles.value[i]?.transform?.match(/translateX\((.*)px\)/)?.[1] ?? '0'
  );
  const currentTranslateY = parseFloat(
    styles.value[i]?.transform?.match(/translateY\((.*)px\)/)?.[1] ?? '0'
  );
  const currentRotateX = parseFloat(
    styles.value[i]?.transform?.match(/rotateX\((.*)deg\)/)?.[1] ?? '0'
  );
  const currentRotateZ = parseFloat(
    styles.value[i]?.transform?.match(/rotateZ\((.*)deg\)/)?.[1] ?? '0'
  );

  const targetTranslateX =
    i % 2 === 0
      ? -translateX.value * percentage
      : translateX.value * percentage;
  const targetTranslateY = translateY * percentage;
  const targetRotateX =
    i % 2 === 0 ? rotateX * percentage : -rotateX * percentage;
  const targetRotateZ =
    i % 2 === 0 ? rotateZ * percentage : -rotateZ * percentage;

  if (
    Math.floor(targetTranslateX) === Math.floor(currentTranslateX) &&
    Math.floor(targetTranslateY) === Math.floor(currentTranslateY) &&
    Math.floor(targetRotateX) === Math.floor(currentRotateX) &&
    Math.floor(targetRotateZ) === Math.floor(currentRotateZ)
  ) {
    cancelAnimationFrame(frames.value[i]);
    return;
  }

  const reverse = targetTranslateX < currentTranslateX;

  const newTranslateX = reverse
    ? Math.max(
        targetTranslateX,
        lerp(currentTranslateX, targetTranslateX, 0.08)
      )
    : Math.min(
        targetTranslateX,
        lerp(currentTranslateX, targetTranslateX, 0.08)
      );

  const newTranslateY = reverse
    ? Math.max(
        targetTranslateY,
        lerp(currentTranslateY, targetTranslateY, 0.08)
      )
    : Math.min(
        targetTranslateY,
        lerp(currentTranslateY, targetTranslateY, 0.08)
      );
  const newRotateX = reverse
    ? Math.max(targetRotateX, lerp(currentRotateX, targetRotateX, 0.08))
    : Math.min(targetRotateX, lerp(currentRotateX, targetRotateX, 0.08));

  const newRotateZ = reverse
    ? Math.max(targetRotateZ, lerp(currentRotateZ, targetRotateZ, 0.08))
    : Math.min(targetRotateZ, lerp(currentRotateZ, targetRotateZ, 0.08));

  styles.value[i] = {
    transform: `translateX(${newTranslateX}px) translateY(${newTranslateY}px) rotateX(${newRotateX}deg) rotateZ(${newRotateZ}deg)`,
  };

  frames.value[i] = requestAnimationFrame(() => update(i));
}

function updateImages(): void {
  refs.value.forEach((_, i) =>
    requestAnimationFrame(() => {
      cancelAnimationFrame(frames.value[i]);
      update(i);
    })
  );
}

function updateBoxShadow(e: MouseEvent): void {
  const mouseX = e.clientX;
  const mouseY = e.clientY;

  refs.value.forEach((ref, i) => {
    const rect = refs.value[i]?.getBoundingClientRect();
    const maxOffset = Math.min(rect.width, rect.height, 40);

    const clampedMouseX = clamp(
      mouseX,
      rect.left - maxOffset,
      rect.right + maxOffset
    );
    const clampedMouseY = clamp(
      mouseY,
      rect.top - maxOffset,
      rect.bottom + maxOffset
    );

    const elementMidX = rect.right - rect.width / 2;
    const elementMidY = rect.bottom - rect.height / 2;

    const normalizedX =
      (clampedMouseX - elementMidX) / (rect.right - rect.left + 2 * maxOffset);
    const normalizedY =
      (clampedMouseY - elementMidY) / (rect.bottom - rect.top + 2 * maxOffset);

    requestAnimationFrame(() => {
      ref.style.boxShadow = `${-1 * normalizedX * maxOffset}px ${
        -1 * normalizedY * maxOffset
      }px 50px 10px rgba(0, 0, 0, 0.1)`;
    });
  });
}

useEventListener('scroll', updateImages);

onMounted(updateImages);
</script>
